import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import { parseMarkdown } from '../../pdf/markdown';
import { DocumentNode, BlockNode, InlineNode } from '../../pdf/markdown/nodes';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

export class TestMarkdown implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    const page = pdf.addPage();
    const font = new PdfFont(PdfFont.FONT_NAME, 14);
    const headingFont = new PdfFont(PdfFont.FONT_NAME, 20);

    const titleRect = new PdfRect(MARGIN, MM(5), PdfSize.A4.width - 2 * MARGIN, MM(8));
    page.drawString(
      'Test Markdown Parser',
      headingFont,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const testMarkdown = `
# Heading 1

This is a paragraph with **bold** and *italic* text.

## Heading 2

- List item 1
- List item 2
- List item 3

### Code

Inline \`code\` and code block.

### Link

[Visit Google](https://google.com)

### Blockquote

> This is a blockquote

### Table

| Column 1 | Column 2 |
|----------|----------|
| Cell 1   | Cell 2   |
`;

    let yPos = 30;

    const ast = parseMarkdown(testMarkdown);
    yPos = renderAST(page, ast.document, font, MM, MARGIN, yPos);
  }
}

function renderAST(
  page: PageGen,
  node: DocumentNode | BlockNode | InlineNode,
  font: PdfFont,
  MM: (mm: number) => number,
  margin: number,
  yPos: number
): number {
  if (node.type === 'document') {
    for (const child of node.children) {
      yPos = renderAST(page, child, font, MM, margin, yPos);
    }
    return yPos;
  }

  if (node.type === 'heading') {
    const headingFont = new PdfFont(PdfFont.FONT_NAME, 24 - node.level * 2);
    const rect = new PdfRect(margin, MM(yPos), PdfSize.A4.width - 2 * margin, MM(8));
    const text = getTextContent(node as any);
    page.drawString(text, headingFont, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);
    return yPos + 10;
  }

  if (node.type === 'paragraph') {
    const text = getInlineText(node.children);
    const rect = new PdfRect(margin, MM(yPos), PdfSize.A4.width - 2 * margin, MM(6));
    page.drawString(text, font, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);
    return yPos + 8;
  }

  if (node.type === 'list') {
    for (const item of node.children) {
      let text = '';
      if (item.children && item.children.length > 0) {
        const firstChild = item.children[0];
        if (firstChild.type === 'paragraph') {
          text = getInlineText((firstChild as any).children || []);
        }
      }
      const rect = new PdfRect(margin + 10, MM(yPos), PdfSize.A4.width - 2 * margin - 10, MM(5));
      page.drawString(`- ${text}`, font, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);
      yPos += 6;
    }
    return yPos + 4;
  }

  if (node.type === 'codeBlock') {
    const rect = new PdfRect(margin, MM(yPos), PdfSize.A4.width - 2 * margin, MM(15));
    page.drawString(
      node.content.substring(0, 50),
      font,
      PdfBrush.Black,
      null,
      rect,
      ETextAlign.CenterLeft
    );
    return yPos + 18;
  }

  if (node.type === 'blockquote') {
    const rect = new PdfRect(margin + 5, MM(yPos), PdfSize.A4.width - 2 * margin - 10, MM(5));
    page.drawString(
      `> ${getBlockContent(node.children)}`,
      font,
      PdfBrush.Black,
      null,
      rect,
      ETextAlign.CenterLeft
    );
    return yPos + 8;
  }

  if (node.type === 'hr') {
    return yPos + 5;
  }

  if (node.type === 'table') {
    const rect = new PdfRect(margin, MM(yPos), PdfSize.A4.width - 2 * margin, MM(10));
    page.drawString(
      `[Table: ${node.headers.join(', ')}]`,
      font,
      PdfBrush.Black,
      null,
      rect,
      ETextAlign.CenterLeft
    );
    return yPos + 15;
  }

  if (node.type === 'module') {
    const rect = new PdfRect(margin, MM(yPos), PdfSize.A4.width - 2 * margin, MM(20));
    page.drawString(
      `[Module: ${node.moduleName}]`,
      font,
      PdfBrush.Black,
      null,
      rect,
      ETextAlign.CenterLeft
    );
    return yPos + 25;
  }

  return yPos;
}

function getTextContent(node: any): string {
  if (node.type === 'text') return node.content || '';
  if (node.type === 'heading') return node.text ? getTextContent(node.text) : '';
  return '';
}

function getInlineText(nodes: InlineNode[]): string {
  return nodes
    .map((n) => {
      if (n.type === 'text') return n.content || '';
      if (n.type === 'bold' || n.type === 'italic') return getInlineText((n as any).children || []);
      if (n.type === 'code') return `\`${n.content}\``;
      if (n.type === 'latex') return `$${n.content}$`;
      if (n.type === 'module') return `<${n.moduleName}>...</${n.moduleName}>`;
      return '';
    })
    .join('');
}

function getBlockContent(nodes: any[]): string {
  return nodes
    .map((n) => {
      if (n.type === 'paragraph') return getInlineText(n.children || []);
      return '';
    })
    .join('');
}
